/**
 * Decides when the map is allowed to frame itself.
 *
 * ONE SHARED CAMERA. Switching tabs never moves the map — where you left it is
 * where you come back to, at the same zoom, in every context. Pilots lost their
 * place because each context re-framed on entry; an earlier attempt "fixed"
 * that by giving every context its own remembered camera, which just traded the
 * problem for tabs sitting at unrelated zoom levels with no continuity between
 * them.
 *
 * Two facts gate an auto-fit, and both must hold:
 *
 *   1. This context has never been framed before — a first visit gets a helpful
 *      frame of its data; later visits leave the camera alone.
 *   2. The user has not positioned the map themselves. Once they have, the map
 *      is theirs and nothing re-frames it again, in any context.
 *
 * Both are session-scoped module state, deliberately not persisted. Losing them
 * on an HMR reload in dev is harmless.
 *
 * Two places frame the camera on context entry and must agree, which is why
 * this is its own module rather than a flag inside either of them:
 *   - `features/map/MapCanvas.tsx`      — drones / oi / infra
 *   - `lib/map/layers/surveillance.ts`  — surv
 */

export const CAMERA_CONTEXTS = ['drones', 'oi', 'infra', 'surv'] as const;
export type CameraContext = (typeof CAMERA_CONTEXTS)[number];

export const isCameraContext = (ctx: string): ctx is CameraContext =>
  (CAMERA_CONTEXTS as readonly string[]).includes(ctx);

/** Contexts already auto-framed from their OWN data. */
const framed = new Set<CameraContext>();

/** Set once the user drags / zooms / rotates. From then on the camera is theirs. */
let userPositioned = false;

/**
 * An auto-fit from this context's own data has landed.
 *
 * Only call this for a fit built from the context's own data. `MapCanvas` shows
 * the fleet bbox as a placeholder while OI's data is still loading; marking
 * that would strand the context on a view it was never meant to keep.
 */
export function markFramed(ctx: string): void {
  if (isCameraContext(ctx)) framed.add(ctx);
}

export function hasBeenFramed(ctx: string): boolean {
  return isCameraContext(ctx) && framed.has(ctx);
}

/** The user moved the map themselves — stop auto-framing it, everywhere. */
export function markUserPositioned(): void {
  userPositioned = true;
}

export function isUserPositioned(): boolean {
  return userPositioned;
}

/** The single question both auto-fitting callers ask before touching the camera. */
export function shouldAutoFit(ctx: string): boolean {
  return isCameraContext(ctx) && !userPositioned && !hasBeenFramed(ctx);
}

/** Test seam — a fresh session has framed nothing and the user has moved nothing. */
export function __resetCameraMemory(): void {
  framed.clear();
  userPositioned = false;
}
